/**
 * @file useComicCatalog.js
 * @description 漫画阅读页 — 目录弹窗与卷元数据
 * @module features/comic/composables/useComicCatalog
 */

import { fetchComicCatalogApi, fetchComicDetailApi } from '../api/comicApi'
import { convertRouteParams } from '@/shared/utils/idHelper'
import { applyComicReaderSeo } from '@/shared/utils/seo'
import { nextTick } from 'vue'
import {
  resolveCatalogVolNum,
  applyGuestCatalogProgressPolicy as applyGuestCatalogProgressPolicyPure,
} from '../utils/catalogVolNum'
import {
  isNeedLoginAccess,
  resolveVolAccessStatus,
  getFirstFreeVolNum,
} from '../utils/volAccess'

/**
 * @param {Record<string, Function>} bridge 共享方法桥
 * @param {object} ctx reactive 状态
 */
export function useComicCatalog(bridge, ctx) {
  async function fetchTotalVolumes() {
    try {
      const response = await fetchComicDetailApi(ctx.comicId, ctx.token)
      if (response.data.code === 200 || response.data.code === 0) {
        const { comicInfoVo, volInfoVos } = response.data.data
        ctx.totalVolumes = comicInfoVo.volTotal || 0
        ctx.comicName = comicInfoVo?.name || ''
        applyComicReaderSeo(comicInfoVo, ctx.volNum)

        // 保存卷信息数组
        ctx.volInfoVos = volInfoVos || []

        // 生成卷号数组 [1, 2, 3, ..., totalVolumes]（保留兼容）
        if (ctx.totalVolumes > 0) {
          ctx.volumeList = Array.from({ length: ctx.totalVolumes }, (_, i) => i + 1)
        } else {
          ctx.volumeList = []
        }

        // 如果 volInfoVos 已加载，但当前卷还没有互动数据，尝试获取
        if (ctx.volInfoVos.length > 0 && !ctx.interactData && !ctx.isLoadingInteract) {
          const activeVol = typeof bridge.getActiveVolNum === 'function'
            ? bridge.getActiveVolNum()
            : Number(ctx.$route?.params?.volNum ?? ctx.routeVolNum ?? ctx.volNum)
          const volId = getVolIdFromVolInfoVos(activeVol)
          if (volId && !ctx.volId) {
            ctx.volId = volId
                        bridge.fetchInteractData().catch(err => {
              console.error('获取互动数据失败:', err)
            })
          }
        }
      }
    } catch (error) {
      console.error('获取总卷数失败:', error)
      ctx.volumeList = []
      ctx.volInfoVos = []
    }
  }

  function shouldPromptLoginForCatalogVol(volNum, volItem) {
    const firstVolNum = getFirstFreeVolNum(ctx.volInfoVos)
    const fromDetail = ctx.volInfoVos?.find((vol) => Number(vol.num) === Number(volNum))
    const vol = volItem || fromDetail || { num: volNum }
    const access = resolveVolAccessStatus(vol, { hasToken: Boolean(ctx.token), firstVolNum })
    return isNeedLoginAccess(access)
  }

  function getVolIdFromVolInfoVos(volNum) {
    if (!ctx.volInfoVos || ctx.volInfoVos.length === 0) {
      console.warn('volInfoVos 为空，无法获取 volId，volNum:', volNum)
      return null
    }

    const volInfo = ctx.volInfoVos.find(vol => vol.num === volNum)
    if (volInfo && volInfo.id) {
            return volInfo.id
    } else {
      console.warn('未找到对应的卷信息，volNum:', volNum, 'volInfoVos 长度:', ctx.volInfoVos.length)
      return null
    }
  }

  async function redirectToTargetVolIfNeeded() {
    const raw = ctx.$route.query.targetVolId
    if (raw === undefined || raw === null || String(raw).trim() === '') {
      return false
    }
    if (!ctx.volInfoVos?.length) return false
    const vol = ctx.volInfoVos.find((v) => String(v.id) === String(raw))
    if (!vol || vol.num === undefined) return false
    const num = Number(vol.num)
    const rest = { ...ctx.$route.query }
    delete rest.targetVolId
    if (num === Number(ctx.volNum)) {
      await ctx.$router.replace({
        name: 'ComicReader',
        params: convertRouteParams({
          comicId: ctx.comicId,
          volNum: num
        }),
        query: rest
      })
      return false
    }
    await ctx.$router.replace({
      name: 'ComicReader',
      params: convertRouteParams({
        comicId: ctx.comicId,
        volNum: num
      }),
      query: rest
    })
    return true
  }

  function getCatalogActiveVolNum() {
    if (ctx.currentProgressVolNum != null && ctx.currentProgressVolNum !== '') {
      return Number(ctx.currentProgressVolNum)
    }
    if (ctx.currentDisplayVolNum != null && ctx.currentDisplayVolNum !== '') {
      return Number(ctx.currentDisplayVolNum)
    }
    if (typeof bridge.getActiveVolNum === 'function') {
      return bridge.getActiveVolNum()
    }
    return Number(ctx.$route?.params?.volNum ?? ctx.routeVolNum ?? ctx.volNum)
  }

  function findCatalogVolItem(volNum) {
    if (!ctx.catalogData?.volArray?.length) {
      return null
    }
    const target = Number(volNum)
    return ctx.catalogData.volArray.find(
      (vol, index) => resolveCatalogVolNum(vol, index) === target,
    ) || null
  }

  /** 目录 volArray 按顺序对应第 1..N 卷，解析并写回 vol.num */
  function normalizeCatalogVolNumbers() {
    if (!ctx.catalogData?.volArray?.length) {
      return
    }
    ctx.catalogData.volArray.forEach((vol, index) => {
      vol.num = resolveCatalogVolNum(vol, index)
    })
  }

  /** 未登录仅允许第 1 卷有阅读进度（目录展示与 localStorage 合并） */
  function applyGuestCatalogProgressPolicy() {
    applyGuestCatalogProgressPolicyPure(ctx.catalogData?.volArray, !!ctx.token)
  }

  /** 将 localStorage 中的阅读进度合并到目录（打开目录时用） */
  function mergeLocalProgressIntoCatalog() {
    if (!ctx.catalogData?.volArray?.length || !ctx.comicId) {
      return
    }
    normalizeCatalogVolNumbers()
    try {
      const allProgress = JSON.parse(localStorage.getItem('reading_progress') || '{}')
      const prefix = `reading_progress_${ctx.comicId}_`
      ctx.catalogData.volArray.forEach((vol, index) => {
        const volNum = resolveCatalogVolNum(vol, index)
        const firstVolNum = getFirstFreeVolNum(ctx.volInfoVos)
        const access = resolveVolAccessStatus(vol, { hasToken: Boolean(ctx.token), firstVolNum })
        if (isNeedLoginAccess(access)) {
          return
        }
        const saved = allProgress[`${prefix}${volNum}`]
        if (!saved || saved.progress == null) {
          return
        }
        const savedProgress = Number(saved.progress)
        const catalogProgress = vol.progress != null ? Number(vol.progress) : -1
        if (savedProgress >= catalogProgress) {
          vol.pageIndex = saved.pageIndex
          vol.progress = savedProgress
        }
      })
    } catch (error) {
      console.error('合并 localStorage 进度到目录失败:', error)
    }
    applyGuestCatalogProgressPolicy()
  }

  /** 保存进度后实时回显到目录 */
  function syncCatalogProgress(volNum, pageIndex, progress) {
    if (shouldPromptLoginForCatalogVol(volNum)) {
      return
    }
    const volItem = findCatalogVolItem(volNum)
    if (!volItem) {
      return
    }
    const nextProgress = Number(progress)
    const prevProgress = volItem.progress != null ? Number(volItem.progress) : -1
    if (nextProgress >= prevProgress) {
      volItem.pageIndex = pageIndex
      volItem.progress = nextProgress
    }
  }

  function scrollCatalogToCurrentVol() {
    const targetVol = getCatalogActiveVolNum()
    const container = ctx.catalogContentRef?.value
      || document.querySelector('.catalog-content')
    if (!container || !ctx.catalogData?.volArray?.length) {
      return
    }

    const index = ctx.catalogData.volArray.findIndex(
      (vol, i) => resolveCatalogVolNum(vol, i) === targetVol,
    )
    if (index < 0) {
      return
    }

    const item = container.querySelector(`[data-catalog-vol-num="${targetVol}"]`)
      || container.querySelectorAll('.catalog-item')[index]
    if (!item) {
      return
    }

    const offsetTop = item.offsetTop - container.clientHeight / 2 + item.clientHeight / 2
    container.scrollTo({
      top: Math.max(0, offsetTop),
      behavior: 'smooth',
    })
  }

  async function toggleCatalog() {
    if (ctx.showCatalog) {
      ctx.showCatalog = false
      return
    }

    if (!ctx.catalogData) {
      await fetchCatalog()
    } else {
      mergeLocalProgressIntoCatalog()
    }

    ctx.showCatalog = true
    await nextTick()
    scrollCatalogToCurrentVol()
  }

  async function fetchCatalog() {
    if (ctx.isLoadingCatalog) {
      return
    }

    try {
      ctx.isLoadingCatalog = true
      const response = await fetchComicCatalogApi(ctx.comicId, ctx.token)

      if (response.data.code === 0 || response.data.code === 200) {
        ctx.catalogData = response.data.data || null
        if (ctx.catalogData) {
          ctx.totalVolumes = ctx.catalogData.volTotal || 0
          normalizeCatalogVolNumbers()
          mergeLocalProgressIntoCatalog()
        }
      } else {
        console.error('获取目录失败:', response.data.msg)
      }
    } catch (error) {
      console.error('请求目录接口失败:', error)
    } finally {
      ctx.isLoadingCatalog = false
    }
  }

  function closeCatalog() {
    ctx.showCatalog = false
  }

  async function goToVolume(volItem) {
    const volNum = getVolNumFromTitle(volItem)

    if (Number(volNum) === getCatalogActiveVolNum()) {
      closeCatalog()
      return
    }

    if (shouldPromptLoginForCatalogVol(volNum, volItem)) {
      ctx.$emit('show-login-popup')
      closeCatalog()
      return
    }

    // 保存当前进度
    if (typeof bridge.saveProgress === 'function') {
      bridge.saveProgress(true)
    }

    // 构建查询参数
    const query = {}
    // 如果目录数据中有 pageIndex，传递 pageIndex 和 restoreProgress 参数
    if (volItem && volItem.pageIndex !== undefined && volItem.pageIndex !== null) {
      query.pageIndex = volItem.pageIndex
      query.restoreProgress = 'true'
          }

    // 跳转到指定卷
    await ctx.$router.push({
      name: 'ComicReader',
      params: convertRouteParams({
        comicId: ctx.comicId,
        volNum: volNum
      }),
      query: query
    })

    await nextTick()

    // 同组件路由跳转：确保卷切换逻辑执行（防止 watch 时序问题）
    if (typeof bridge.applyVolumeSwitch === 'function') {
      bridge.applyVolumeSwitch(volNum, 'catalog')
    }

    // 关闭目录
    closeCatalog()
  }

  function getVolNumFromTitle(titleOrVol, index) {
    if (typeof titleOrVol === 'number') {
      return titleOrVol
    }
    if (titleOrVol && typeof titleOrVol === 'object') {
      return resolveCatalogVolNum(titleOrVol, index)
    }
    return resolveCatalogVolNum(titleOrVol, index)
  }

  function formatDate(dateTimeString) {
    if (!dateTimeString) return ''
    // 如果包含空格，只取日期部分（空格前的部分）
    const datePart = dateTimeString.split(' ')[0]
    return datePart
  }

  function getProgressOffset(progress, radius = 12) {
    // progress 是 0-1 之间的小数
    // 圆周长 = 2 * π * r
    const circumference = 2 * Math.PI * radius
    // 计算已完成的长度
    const offset = circumference * (1 - progress)
    return offset
  }

  Object.assign(bridge, {
    fetchTotalVolumes,
    getVolIdFromVolInfoVos,
    redirectToTargetVolIfNeeded,
    toggleCatalog,
    fetchCatalog,
    closeCatalog,
    goToVolume,
    getVolNumFromTitle,
    formatDate,
    getProgressOffset,
    getCatalogActiveVolNum,
    syncCatalogProgress,
    mergeLocalProgressIntoCatalog,
    scrollCatalogToCurrentVol,
    shouldPromptLoginForCatalogVol,
  })
}
